import { Domain as AuthDomain } from 'auth-common/domain';
import type { Tagged, ValueOf } from 'type-fest';
import { ImmutableEntity } from '../../common';
import type { CommandContext } from '../../common/context';
import { UniqueShortId } from '../../common/id';

import Project = AuthDomain.Object.Project;

const uuidGenerator = UniqueShortId.GetFactory(21);

/** アップロード元 */
const _UploadSource = {
  Upload: 'Upload',
  Collector: 'Collector',
} as const;
type _UploadSource = ValueOf<typeof _UploadSource>;

export type FileMetadata = ImmutableEntity & {
  projectId: Project.Id;
  id: FileMetadata.Id;
  fileName: string;
  fileSize: bigint;
  contentType: string;
  gcsPath: string;
  uploadSource: _UploadSource;
  /**
   * GCS オブジェクトのエンコーディング。`'gzip'` のとき、GCS には gzip 圧縮されたバイト列が
   * そのまま保存されている (Content-Encoding は付与しない方式)。読み取り側は明示的に gunzip する。
   * 非圧縮 (既存ファイル / Collector 経由) では undefined。
   */
  contentEncoding?: string;
};

export namespace FileMetadata {
  export type Id = Tagged<UniqueShortId, 'fileMetadataId'>;
  export namespace Id {
    export const Generate = () => uuidGenerator.generate() as Id;
    export function FromString(id: string): Id {
      return uuidGenerator.FromString(id) as Id;
    }
  }

  export type UploadSource = _UploadSource;
  export namespace UploadSource {
    export const FromString = (s: string): UploadSource => {
      if (s === _UploadSource.Upload || s === _UploadSource.Collector) {
        return s;
      }
      throw new Error(`Invalid UploadSource: ${s}`);
    };
  }

  export namespace Construct {
    export type Props = {
      id: string;
      projectId: string;
      fileName: string;
      fileSize: bigint | number | string;
      contentType: string;
      gcsPath: string;
      uploadSource: string;
      contentEncoding?: string | null;
    } & ImmutableEntity.Props;
  }

  export const Construct = (props: Construct.Props): FileMetadata => {
    return {
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      fileName: props.fileName,
      fileSize: BigInt(props.fileSize),
      contentType: props.contentType,
      gcsPath: props.gcsPath,
      uploadSource: UploadSource.FromString(props.uploadSource),
      contentEncoding: props.contentEncoding ?? undefined,
      ...ImmutableEntity.Construct(props),
    };
  };

  export namespace Create {
    export type Context = CommandContext;
    export type Props = Pick<
      Construct.Props,
      | 'fileName' //
      | 'fileSize'
      | 'contentType'
      | 'gcsPath'
      | 'uploadSource'
      | 'contentEncoding'
    >;
    export type Params = {
      context: Context;
      props: Props;
    };
  }
  export const Create = (params: Create.Params): FileMetadata => {
    const { context, props } = params;
    return Construct({ id: Id.Generate(), projectId: context.projectId, ...props, ...ImmutableEntity.Create(context) });
  };
}
